import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Mic, Volume2 } from "lucide-react";
import { chunkScript } from "../data/training_scripts";

/* LessonVideoPlayer — Mike's photo on the left, audio player + scrolling
   typewriter captions on the right.

   Sync method (auto-pacing, zero LLM cost):
     - Script is pre-chunked into ~3-word chips
     - We listen to the audio element's timeupdate event
     - chip_duration = audio.duration / chunks.length
     - active_index = floor(currentTime / chip_duration)
     - Chips up to active_index get the "spoken" style
     - The active chip animates in (typewriter feel)

   When no audioUrl yet: shows the headshot + the full script as readable
   text + a friendly "Audio recording coming soon" notice. */

const ACCENT_DEFAULTS = { accent: "#2E9F3A" };

export default function LessonVideoPlayer({ script, headshot, accent }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const a = accent || ACCENT_DEFAULTS.accent;

  const chunks = useMemo(
    () => (script?.script ? chunkScript(script.script, 3) : []),
    [script]
  );

  const safeDuration = duration || script?.durationSec || 0;
  const chipMs = chunks.length && safeDuration ? (safeDuration * 1000) / chunks.length : 0;
  const activeIndex = chipMs ? Math.floor((currentTime * 1000) / chipMs) : -1;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrentTime(a.currentTime || 0);
    const onLoaded = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, [script?.audioUrl]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const restart = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    setCurrentTime(0);
  };

  if (!script) {
    return (
      <div className="rounded-2xl border-2 p-6 mb-6 bg-white text-center"
           style={{ borderColor: "#E5DDC8" }} data-testid="lesson-video-no-script">
        <p className="text-base text-foreground/65 italic">
          Audio version coming soon. Read the written lesson below.
        </p>
      </div>
    );
  }

  const hasAudio = !!script.audioUrl;
  const progressPct = safeDuration ? (currentTime / safeDuration) * 100 : 0;

  return (
    <div className="rounded-2xl border-2 overflow-hidden mb-6 shadow-md"
         style={{ borderColor: `${a}66`, background: "#FFFFFF" }}
         data-testid="lesson-video-player">
      <div className="grid md:grid-cols-[260px_1fr]">
        {/* Mike's photo */}
        <div className="relative overflow-hidden flex items-center justify-center"
             style={{ background: `linear-gradient(180deg, ${a}11 0%, ${a}22 100%)`, minHeight: 280 }}>
          {headshot ? (
            <img src={headshot}
                 alt="Mike Boyd — Faitharama founder"
                 onError={(e) => { e.target.style.display = "none"; }}
                 className="w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
               style={{ background: headshot ? "transparent" : `${a}15` }}>
            {!headshot && <Mic className="w-16 h-16" style={{ color: a, opacity: 0.35 }} />}
          </div>
          <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                style={{ background: a }}>
            ★ Mike Boyd
          </span>
        </div>

        {/* Caption rail */}
        <div className="p-5 md:p-6 flex flex-col justify-between">
          {hasAudio ? (
            <div className="min-h-[140px] flex flex-wrap gap-x-2 gap-y-1 content-start"
                 data-testid="lesson-video-captions">
              {chunks.map((chip, i) => {
                const isSpoken = i < activeIndex;
                const isActive = i === activeIndex;
                return (
                  <span key={i}
                        className="text-lg md:text-xl leading-relaxed transition-all duration-300"
                        style={{
                          color: isSpoken ? "#0F172A" : isActive ? a : "#0F172A55",
                          fontWeight: isActive ? 700 : 500,
                          textShadow: isActive ? `0 0 12px ${a}33` : "none",
                        }}>
                    {chip}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="min-h-[140px]">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                   style={{ background: `${a}15`, color: a }}>
                <Mic className="w-3 h-3" /> Audio Coming Soon
              </div>
              <p className="text-base md:text-lg leading-relaxed text-foreground/80 italic">
                "{script.script}"
              </p>
            </div>
          )}

          {/* Controls */}
          {hasAudio && (
            <div className="mt-5 pt-4 border-t" style={{ borderColor: `${a}22` }}>
              <audio ref={audioRef} src={script.audioUrl} preload="metadata" />
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  data-testid="lesson-video-play"
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow hover:scale-105 transition flex-shrink-0"
                  style={{ background: a }}>
                  {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <button
                  onClick={restart}
                  data-testid="lesson-video-restart"
                  title="Restart"
                  className="w-9 h-9 rounded-full flex items-center justify-center border-2 hover:bg-white transition flex-shrink-0"
                  style={{ borderColor: a, color: a }}>
                  <RotateCcw className="w-4 h-4" />
                </button>
                <div className="flex-1">
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: `${a}22` }}>
                    <div className="h-full transition-all duration-200"
                         style={{ background: a, width: `${progressPct}%` }} />
                  </div>
                  <div className="flex justify-between text-[11px] mt-1 text-foreground/55">
                    <span>{fmt(currentTime)}</span>
                    <span>{fmt(safeDuration)}</span>
                  </div>
                </div>
                <Volume2 className="w-4 h-4 text-foreground/40 hidden md:inline" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmt(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}
